import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SecureSessionService } from './secure-session.service';

export interface MaintenanceAlertItem {
  key: string;
  title: string | null;
  message: string;
  type: 'maintenance' | 'sms';
}

@Injectable({ providedIn: 'root' })
export class MaintenanceAlertService {

  private readonly STORAGE_KEY = 'sms_dismissed_maintenance_alerts';

  private readonly _alerts$ = new BehaviorSubject<MaintenanceAlertItem[]>([]);
  readonly alerts$ = this._alerts$.asObservable();

  private orgCode: number | null = null;
  // prevent duplicate HTTP calls within the same login session
  private loaded = false;

  constructor(private readonly http: HttpClient, private readonly session: SecureSessionService) {}

  /** Store orgCode fetched at app init — safe to call regardless of login state. */
  setOrgCode(orgCode: number): void {
    this.orgCode = orgCode;
  }

  /**
   * Call on every NavigationEnd event.
   * Fetches alerts only when the user has an active session and hasn't loaded yet this session.
   */
  tryLoad(): void {
    if (this.loaded || !this.orgCode) return;
    try {
      const sess = this.session.getSession();
      if (!sess) return; // not logged in — wait for next navigation
      const sessionData = JSON.parse(sess);
      const roles: string[] = sessionData?.[0]?.roles ?? [];
      if (!roles.includes('super_admin')) return; // alerts are admin-only
    } catch {
      return;
    }
    this.loaded = true;
    this.fetchAlerts(this.orgCode);
  }

  /** Reset state on logout so alerts reload fresh on next login. */
  reset(): void {
    this.loaded = false;
    this._alerts$.next([]);
  }

  dismiss(key: string): void {
    const dismissed = this.getDismissed();
    if (!dismissed.includes(key)) {
      dismissed.push(key);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dismissed));
    }
    const remaining = this._alerts$.getValue().filter(a => a.key !== key);
    this._alerts$.next(remaining);
  }

  private fetchAlerts(orgCode: number): void {
    const url = `${environment.baseUrl}api/Gds/maintenance/${orgCode}`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (!res?.success) return;
        const alerts: MaintenanceAlertItem[] = [];
        const maintenance: any[] = res?.data?.maintenance ?? [];
        for (const m of maintenance) {
          const key = `maintenance_${m.id}`;
          if (!this.isDismissed(key)) {
            alerts.push({ key, title: 'Maintenance Notice', message: m.message, type: 'maintenance' });
          }
        }
        const smsMsg: string = res?.data?.smsAlert?.message ?? '';
        if (smsMsg) {
          // key derived from message so a new message is treated as a new alert
          const key = `sms_${this.hashStr(smsMsg)}`;
          if (!this.isDismissed(key)) {
            alerts.push({ key, title: 'SMS Balance Alert', message: smsMsg, type: 'sms' });
          }
        }
        this._alerts$.next(alerts);
      },
      error: () => { /* non-critical — silently ignore */ }
    });
  }

  private isDismissed(key: string): boolean {
    return this.getDismissed().includes(key);
  }

  private getDismissed(): string[] {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY) ?? '[]');
    } catch {
      return [];
    }
  }

  // djb2-style hash — stable short key derived from a string
  private hashStr(s: string): string {
    let h = 5381;
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) + h) ^ (s.codePointAt(i) ?? 0);
      h = h >>> 0;
    }
    return h.toString(36);
  }
}
